import { Users, ArrowRightLeft, FlaskConical, LineChart, MessageSquare, FileCode2, ClipboardList, Eye, Play, Search, Target, TrendingUp, BarChart3, type LucideIcon } from "lucide-react";
import { AnimatedCounter } from "../shared/AnimatedCounter";
import { ScrollWords } from "../shared/ScrollWords";
import { motion } from "motion/react";
import { SectionLabel, H2, Body } from "../shared/Typography";
import { useSectionParallax } from "../../hooks/useSectionParallax";

const VIOLET = "text-[#5B21B6] dark:text-[#B9A5FF]";
const DECISIONS = ["Starting dose", "Escalation rule", "Cohort size", "Stopping criteria"];

export function WhySimulate() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref} className="grid lg:grid-cols-2 gap-14 items-center">
      <motion.div style={{ y: fgY }}>
        <SectionLabel tone="foresight">Why simulate first</SectionLabel>
        <H2 className="mb-5">A protocol is a set of decisions made under uncertainty.</H2>
        <Body className="mb-4">
          The starting dose, the escalation rule, the cohort size, the stopping criteria. Once patients
          are enrolled, those decisions are slow and costly to change.
        </Body>
        <Body>
          Foresight moves the testing earlier. Run the design against a model of your drug and a cohort
          of digital twins, see where it strains, change it, and run it again. The same design can be run
          many times, so you learn how consistently it reaches the right decision, not just what happened once.
        </Body>
      </motion.div>
      <motion.div style={{ y: bgY }} className="grid grid-cols-2 gap-4">
        {DECISIONS.map((d, i) => (
          <div key={d} className="rounded-2xl border border-[#5B21B6]/20 dark:border-[#7C4DFF]/25 bg-white dark:bg-[#0D0D10] p-6">
            <p className={`font-mono text-xs ${VIOLET}`}>0{i + 1}</p>
            <p className="mt-3 text-lg font-normal text-[#0A0A0F] dark:text-white">{d}</p>
            <p className="mt-1 text-xs text-[#5C5C66] dark:text-[#9A9A9A]">Tested before enrollment</p>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

// Same vertical pipeline layout as the previous "One pipeline" section, with the five services.
const SERVICES: { name: string; body: string; icon: LucideIcon; cls: string }[] = [
  { name: "Athena", body: "Generates digital twin cohorts from your inclusion and exclusion criteria.", icon: Users, cls: "border-[#5B21B6] text-[#5B21B6] dark:border-[#7C4DFF] dark:text-[#B9A5FF]" },
  { name: "Eigen", body: "Brings the pharmacology models you already have into Foresight.", icon: ArrowRightLeft, cls: "border-[#2D6A52] text-[#2D6A52] dark:border-[#3FA37A] dark:text-[#7ED4AF]" },
  { name: "Simulator", body: "Runs your trial design and tracks every job from submission to results.", icon: FlaskConical, cls: "border-[#8B3A7E] text-[#8B3A7E] dark:border-[#C158AF] dark:text-[#E5A6DA]" },
  { name: "iViz", body: "Explores, visualizes and annotates patient data.", icon: LineChart, cls: "border-[#1E4FD8] text-[#1E4FD8] dark:border-[#4A90D9] dark:text-[#8CC0F0]" },
  { name: "BEAM", body: "A conversational analysis agent: describe the analysis, and it runs.", icon: MessageSquare, cls: "border-[#C9A55E] text-[#C9A55E] dark:border-[#C9A55E] dark:text-[#E0C088]" },
];

const TextOnly = (cls: string) => cls.split(" ").filter((c) => c.startsWith("text-") || c.startsWith("dark:text-")).join(" ");

export function FiveServices() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref}>
      <motion.div style={{ y: fgY }} className="text-center mb-12">
        <div className="flex justify-center"><SectionLabel tone="foresight">The platform</SectionLabel></div>
        <H2>Five services, one platform.</H2>
      </motion.div>
      <motion.ol style={{ y: bgY }} className="max-w-2xl mx-auto">
        {SERVICES.map(({ name, body, icon: Icon, cls }, i) => (
          <li key={name} className="relative flex gap-6 pb-10 last:pb-0">
            {i < SERVICES.length - 1 && <div className="absolute left-[19px] top-10 bottom-0 w-px bg-[#0A0A0F]/10 dark:bg-white/10" />}
            <div className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 bg-[#FAFAFA] dark:bg-[#050505] ${cls}`}>
              <Icon size={20} />
            </div>
            <div className="pt-1.5">
              <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${TextOnly(cls)}`}>0{i + 1} · {name}</p>
              <Body className="text-sm max-w-lg">{body}</Body>
            </div>
          </li>
        ))}
      </motion.ol>
    </div>
  );
}

const STEPS: { title: string; body: string; icon: LucideIcon }[] = [
  { title: "Bring your model.", body: "Convert an existing model with Eigen.", icon: FileCode2 },
  { title: "Build the cohort.", body: "Generate digital twins with Athena, or bring your own patient data.", icon: Users },
  { title: "Design the trial.", body: "Set doses, schedules, observations and decision rules.", icon: ClipboardList },
  { title: "Review before anything runs.", body: "Foresight shows the schedule your design produces, and the run starts only after you confirm it.", icon: Eye },
  { title: "Simulate.", body: "Submit the job and follow it through to completion.", icon: Play },
  { title: "Explore and ask.", body: "Open the results in iViz, or put questions to BEAM in plain language.", icon: Search },
];

export function StudyFlow() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref}>
      <motion.div style={{ y: fgY }} className="text-center mb-12">
        <div className="flex justify-center"><SectionLabel tone="foresight">How it works</SectionLabel></div>
        <H2>How a study moves through Foresight.</H2>
      </motion.div>
      <motion.ol style={{ y: bgY }} className="max-w-2xl mx-auto">
        {STEPS.map(({ title, body, icon: Icon }, i) => (
          <li key={title} className="relative flex gap-6 pb-10 last:pb-0">
            {i < STEPS.length - 1 && <div className="absolute left-[19px] top-10 bottom-0 w-px bg-[#5B21B6]/25 dark:bg-[#7C4DFF]/30" />}
            <div className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 border-[#5B21B6] dark:border-[#7C4DFF] bg-[#FAFAFA] dark:bg-[#050505] ${VIOLET}`}>
              <Icon size={18} />
            </div>
            <div className="pt-1.5">
              <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${VIOLET}`}>Step 0{i + 1}</p>
              <p className="text-base font-medium text-[#0A0A0F] dark:text-white">{title}</p>
              <Body className="text-sm mt-1">{body}</Body>
            </div>
          </li>
        ))}
      </motion.ol>
    </div>
  );
}

// Restored from the previous Foresight page.
const PROBLEM_STATS = [
  { stat: 90, suffix: "%", label: "of Phase I drugs never reach approval", icon: Target },
  { stat: 500, prefix: "$", suffix: "M+", label: "average cost of a failed clinical trial", icon: TrendingUp },
  { stat: 50, suffix: "%", label: "of Phase II failures stem from incorrect dose selection", icon: BarChart3 },
];

export function ProblemSection() {
  return (
    <div>
      <div className="text-center mb-10">
        <SectionLabel tone="foresight">The Problem</SectionLabel>
        <ScrollWords
          text="Many failures are determined before the trial begins"
          className="text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.05] tracking-[-0.02em] text-[#0A0A0F] dark:text-[#F5F5F7]"
        />
      </div>
      <div>
      <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#0A0A0F]/10 dark:divide-white/10 mb-6">
        {PROBLEM_STATS.map(({ stat, suffix, prefix, label, icon: Icon }, i) => (
          <div key={label} className={`py-8 md:py-0 ${i > 0 ? "md:pl-10" : ""} ${i < PROBLEM_STATS.length - 1 ? "md:pr-10" : ""}`}>
            <Icon size={20} className="text-[#5B21B6] dark:text-[#B9A5FF] mb-5" />
            <p className="text-5xl font-normal tracking-[-0.02em] mb-3 text-[#0A0A0F] dark:text-white">
              <AnimatedCounter target={stat} suffix={suffix} prefix={prefix ?? ""} />
              <sup className="text-base align-super ml-0.5">¹</sup>
            </p>
            <p className="text-sm font-medium text-[#5C5C66] dark:text-[#9A9A9A]">{label}</p>
          </div>
        ))}
      </div>
      <p className="text-center text-[11px] text-[#5C5C66]/70 dark:text-[#9A9A9A]/70 mb-14">
        ¹ Industry-reported estimates. Sources available on request.
      </p>
      </div>
      <div className="max-w-3xl mx-auto text-center">
        <Body className="text-lg">
          Protocol teams must commit to doses, patient populations, endpoints, and trial structure
          while substantial biological and pharmacological uncertainty still remains. Foresight
          exists to reduce that uncertainty before human trial commitments are made.
        </Body>
      </div>
    </div>
  );
}
